import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PedidoComentario {
  id: number;
  pedido_id: number;
  usuario_id?: string | null;
  conteudo: string;
  created_at: string;
  updated_at: string;
  usuario?: {
    id: string;
    nome?: string | null;
    email?: string | null;
  } | null;
}

export function usePedidoComentarios(pedidoId: number | null, enabled = true) {
  return useQuery({
    queryKey: ["pedido-comentarios", pedidoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pedido_comentarios")
        .select("*")
        .eq("pedido_id", pedidoId!)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar comentários do pedido:", error);
        throw error;
      }

      const rows = (data || []) as PedidoComentario[];
      const userIds = Array.from(
        new Set(rows.map((r) => r.usuario_id).filter(Boolean) as string[])
      );

      if (userIds.length === 0) return rows;

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, nome, email")
        .in("id", userIds);

      const byId = new Map((profiles || []).map((p) => [p.id, p]));
      return rows.map((r) => ({
        ...r,
        usuario: r.usuario_id ? byId.get(r.usuario_id) || null : null,
      }));
    },
    enabled: enabled && !!pedidoId,
    staleTime: 30_000,
  });
}

export function useCriarPedidoComentario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pedidoId,
      conteudo,
    }: {
      pedidoId: number;
      conteudo: string;
    }) => {
      const texto = conteudo.trim();
      if (!texto) throw new Error("Informe o comentário.");

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("pedido_comentarios")
        .insert({
          pedido_id: pedidoId,
          usuario_id: user?.id ?? null,
          conteudo: texto,
        })
        .select("*")
        .single();

      if (error) {
        console.error("Erro ao criar comentário:", error);
        throw new Error(error.message || "Erro ao criar comentário");
      }

      return data as PedidoComentario;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["pedido-comentarios", data.pedido_id],
      });
    },
  });
}

export function useExcluirPedidoComentario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      pedidoId,
    }: {
      id: number;
      pedidoId: number;
    }) => {
      const { error } = await supabase
        .from("pedido_comentarios")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Erro ao excluir comentário:", error);
        throw new Error(error.message || "Erro ao excluir comentário");
      }

      return { id, pedidoId };
    },
    onSuccess: ({ pedidoId }) => {
      queryClient.invalidateQueries({
        queryKey: ["pedido-comentarios", pedidoId],
      });
    },
  });
}
